import React, { Fragment, useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  message,
  Popconfirm,
  Row,
  Select,
  Spin,
  Switch,
  Typography,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { loadCategories } from "../../../application/categories-slice";
import { loadProducts } from "../../../application/products-slice";
import { useAppDispatch, useAppSelector } from "../../../application/store";
import {
  ProductImage,
  NewProductAttribute,
  Product,
  ProductsRepository,
  Sort,
  isinstanceofProductImage,
} from "../../../domain";
import { ServiceLocater, Services } from "../../../service-locater";
import { logger } from "../../../utils";
import I18n from "../../i18n";
import NewAttributeModal from "./components/NewAttributeModal";
import { useQuery } from "../../hooks";
import EditAttributeModal from "./components/EditAttributeModal";
import { loadColors } from "../../../application/colors-slice";
import SetPriceModal from "./components/SetPriceModal";
import SetOffPriceModal from "./components/SetOffPriceModal";

const { useForm, Item } = Form;
const { Text } = Typography;

export default function EditProduct() {
  const query = useQuery();
  const id = query.get("id");
  const dispatch = useAppDispatch();
  const [form] = useForm();
  const { loading, categories } = useAppSelector((state) => state.categories);

  const stableDispacth = useCallback(dispatch, []);
  useEffect(() => {
    stableDispacth(loadCategories());
  }, [stableDispacth]);

  useEffect(() => {
    stableDispacth(loadColors());
  }, [stableDispacth]);

  const [product, setProduct] = useState<Product>();
  const [isLoadingProduct, setIsLoadingProduct] = useState(false);
  const loadProduct = async () => {
    if (loading === "succeeded" && id !== null) {
      setIsLoadingProduct(true);
      const res = await ServiceLocater.get<ProductsRepository>(
        Services.ProductsRepository
      ).getProductInfo(id);

      res.fold(
        (_) => {
          message.error(I18n.general.networkError);
        },
        (r) => {
          const imagesCopy = new Array(16);
          imagesCopy.fill(undefined);
          for (let index = 0; index < r.images.length; index++) {
            const image = r.images[index];
            imagesCopy[image.index] = image;
          }
          setImages(imagesCopy);
          setProduct(r);
          setAttributes(r.attributes);
          setSelectedCategories({
            generalCategoryId: r.generalCategory?.id,
            subCategoryId: r.subCategory?.id,
            categoryId: r.category?.id,
          });
          form.setFields([
            { name: "generalCategory", value: r.generalCategory?.id },
            { name: "subCategory", value: r.subCategory?.id },
            { name: "category", value: r.category?.id },
            { name: "title", value: r.title },
            { name: "description", value: r.description },
          ]);
        }
      );
      setIsLoadingProduct(false);
    }
  };

  const stableLoadProduct = useCallback(loadProduct, [id, loading]);
  useEffect(() => {
    stableLoadProduct();
  }, [stableLoadProduct]);

  const [isLoading, setIsLoading] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<{
    generalCategoryId?: string;
    subCategoryId?: string;
    categoryId?: string;
  }>({});

  const [newAttributeModalVisible, setNewAttributeModalVisible] = useState(
    false
  );
  const openNewAttributeModal = () => setNewAttributeModalVisible(true);
  const closeNewAttributeModal = () => setNewAttributeModalVisible(false);

  const [editAttributeModal, setEditAttributeModal] = useState<{
    visible: boolean;
    attribute?: NewProductAttribute;
  }>({ visible: false });
  const openEditAttributeModal = (attribute: NewProductAttribute) =>
    setEditAttributeModal({ visible: true, attribute });
  const closeEditAttributeModal = (refresh: boolean) => {
    if (refresh) loadProduct();
    setEditAttributeModal({ visible: false, attribute: undefined });
  };

  const defaultImages = new Array(16);
  defaultImages.fill(undefined);
  const [images, setImages] = useState<Array<ProductImage | undefined>>(
    defaultImages
  );

  const [areImagesLoading, setAreImagesLoading] = useState(false);

  const deleteImage = async (index: number) => {
    if (isinstanceofProductImage(images[index]) === false) {
      const imagesCopy = [...images];
      imagesCopy[index] = undefined;
      setImages(imagesCopy);
    } else {
      const hide = message.loading("در حال حذف عکس...", 20);
      setAreImagesLoading(true);
      const res = await ServiceLocater.get<ProductsRepository>(
        Services.ProductsRepository
      ).deleteImageFromProduct((images[index] as ProductImage).id);
      setAreImagesLoading(false);
      hide();
      res.fold(
        (l) => {
          message.error(I18n.general.networkError);
        },
        (r) => {
          message.success("عکس با موفقیت حذف شد.");
          loadProduct();
        }
      );
    }
  };

  const addImage = async (image: File, index: number) => {
    if (id === null) return;
    const hide = message.loading("در حال افزودن عکس...", 20);
    setAreImagesLoading(true);
    const res = await ServiceLocater.get<ProductsRepository>(
      Services.ProductsRepository
    ).addImageToProduct(id, image, index);
    setAreImagesLoading(false);
    hide();
    res.fold(
      (l) => {
        message.error(I18n.general.networkError);
      },
      (r) => {
        message.success("عکس با موفقیت افزوده شد.");
        loadProduct();
      }
    );
  };

  const editImage = async (image: File, index: number) => {
    if (id === null) return;
    const hide = message.loading("در حال ویرایش عکس...", 20);
    setAreImagesLoading(true);
    const res = await ServiceLocater.get<ProductsRepository>(
      Services.ProductsRepository
    ).editProductImage(images[index]!.id, image);
    setAreImagesLoading(false);
    hide();
    res.fold(
      (l) => {
        message.error(I18n.general.networkError);
      },
      (r) => {
        message.success("عکس با موفقیت ویرایش شد.");
        loadProduct();
      }
    );
  };

  const [attributes, setAttributes] = useState<NewProductAttribute[]>([]);
  const addAttribute = (attribute: NewProductAttribute) => {
    form.setFields([{ name: "attributes", value: "attribute" }]);
    setAttributes([...attributes, attribute]);
  };

  const [setPriceModal, setSetPriceModal] = useState<{
    visible: boolean;
    productId?: string;
  }>({ visible: false });
  const openSetPriceModal = (productId: string) =>
    setSetPriceModal({ visible: true, productId });
  const closeSetPriceModal = (refresh: boolean) => {
    if (refresh) loadProduct();
    setSetPriceModal({ visible: false, productId: undefined });
  };

  const [setOffPriceModal, setSetOffPriceModal] = useState<{
    visible: boolean;
    productId?: string;
  }>({ visible: false });
  const openSetOffPriceModal = (productId: string) =>
    setSetOffPriceModal({ visible: true, productId });
  const closeSetOffPriceModal = (refresh: boolean) => {
    if (refresh) loadProduct();
    setSetOffPriceModal({ visible: false, productId: undefined });
  };

  const submit = async () => {
    try {
      await form.validateFields();
      if (id === null) return;
      setIsLoading(true);
      const res = await ServiceLocater.get<ProductsRepository>(
        Services.ProductsRepository
      ).editProductInfo({
        id: id,
        active: form.getFieldValue("active"),
        title: form.getFieldValue("title"),
        description: form.getFieldValue("description"),
        label: form.getFieldValue("label"),
        generalCategoryId: form.getFieldValue("generalCategory"),
        subCategoryId: form.getFieldValue("subCategory"),
        categoryId: form.getFieldValue("category"),
      });
      setIsLoading(false);
      res.fold(
        (_) => {
          message.error(I18n.general.networkError);
        },
        (_) => {
          dispatch(loadProducts(1, Sort.date));
          message.success("اطلاعات محصول با موفقیت ویرایش شد");
        }
      );
    } catch (error) {
      logger.error(error);
    }
  };

  const renderSubCategories = () => {
    if (selectedCategories.generalCategoryId === undefined) return <Fragment />;
    return categories
      .find(
        (generalCategory) =>
          generalCategory.id === selectedCategories.generalCategoryId
      )
      ?.subCategories.map((subCategory) => (
        <Select.Option key={subCategory.id} value={subCategory.id}>
          {subCategory.title}
        </Select.Option>
      ));
  };

  const renderCategories = () => {
    if (selectedCategories.subCategoryId === undefined) return <Fragment />;
    return categories
      .find(
        (generalCategory) =>
          generalCategory.id === selectedCategories.generalCategoryId
      )
      ?.subCategories.find(
        (subCategory) => subCategory.id === selectedCategories.subCategoryId
      )
      ?.categories.map((category) => (
        <Select.Option value={category.id}>{category.title}</Select.Option>
      ));
  };

  if (loading !== "succeeded" || isLoadingProduct) {
    return (
      <Row
        justify="center"
        align="middle"
        style={{ width: "100%", height: 300 }}
      >
        <Spin spinning={loading === "pending"} />
      </Row>
    );
  }
  return (
    <div style={{ margin: "16px 24px" }}>
      <Form form={form} layout="vertical">
        <Item
          name="title"
          label={I18n.product.title}
          required
          rules={[
            { required: true, validateTrigger: "onSubmit", type: "string" },
          ]}
        >
          <Input name="title" maxLength={100} defaultValue={product?.title} />
        </Item>
        <Item
          name="description"
          label={I18n.product.description}
          required
          rules={[
            { required: true, validateTrigger: "onSubmit", type: "string" },
          ]}
        >
          <Input.TextArea
            name="description"
            inputMode="text"
            rows={4}
            defaultValue={product?.description}
          />
        </Item>
        <Item name="label" label="هایلایت">
          <Input name="label" inputMode="text" defaultValue={product?.label} />
        </Item>
        <Row>
          <Col lg={8} md={8} style={{ paddingLeft: 8 }}>
            <Item name="generalCategory" label={I18n.product.generalCategory}>
              <Select
                placeholder={I18n.product.generalCategory}
                allowClear
                onClear={() => {
                  setSelectedCategories({});
                  form.setFields([
                    { name: "generalCategory", value: undefined },
                    { name: "subCategory", value: undefined },
                    { name: "category", value: undefined },
                  ]);
                }}
                onSelect={(value, option) => {
                  setSelectedCategories({ generalCategoryId: value as string });
                  form.setFields([
                    { name: "subCategory", value: undefined },
                    { name: "category", value: undefined },
                  ]);
                }}
              >
                {categories.map((generalCategory) => (
                  <Select.Option
                    key={generalCategory.id}
                    value={generalCategory.id}
                  >
                    {generalCategory.title}
                  </Select.Option>
                ))}
              </Select>
            </Item>
          </Col>
          <Col lg={8} md={8} style={{ padding: "0 8px" }}>
            <Item name="subCategory" label={I18n.product.subCategory}>
              <Select
                placeholder={I18n.product.subCategory}
                allowClear
                onClear={() => {
                  setSelectedCategories({
                    generalCategoryId: selectedCategories.generalCategoryId,
                  });
                  form.setFields([
                    { name: "subCategory", value: undefined },
                    { name: "category", value: undefined },
                  ]);
                }}
                disabled={selectedCategories.generalCategoryId === undefined}
                onSelect={(value, option) => {
                  setSelectedCategories({
                    generalCategoryId: selectedCategories.generalCategoryId,
                    subCategoryId: value as string,
                  });
                  form.setFields([{ name: "category", value: undefined }]);
                }}
              >
                {renderSubCategories()}
              </Select>
            </Item>
          </Col>
          <Col lg={8} md={8} style={{ paddingRight: 8 }}>
            <Item name="category" label={I18n.product.category}>
              <Select
                placeholder={I18n.product.category}
                allowClear
                onClear={() => {
                  setSelectedCategories({
                    generalCategoryId: selectedCategories.generalCategoryId,
                    subCategoryId: selectedCategories.subCategoryId,
                  });
                  form.setFields([{ name: "category", value: undefined }]);
                }}
                disabled={selectedCategories.subCategoryId === undefined}
                onSelect={(value, option) =>
                  setSelectedCategories({
                    generalCategoryId: selectedCategories.generalCategoryId,
                    subCategoryId: selectedCategories.subCategoryId,
                    categoryId: value as string,
                  })
                }
              >
                {renderCategories()}
              </Select>
            </Item>
          </Col>
        </Row>
        <Item name="active" label="فعال">
          <Switch defaultChecked={product?.active} />
        </Item>
        <Row>
          <Col lg={8} md={8} />
          <Col lg={8} md={8} style={{ padding: "0 8px" }}>
            <Item>
              <Button type="primary" block onClick={submit} loading={isLoading}>
                {I18n.general.submit}
              </Button>
            </Item>
          </Col>
          <Col lg={8} md={8} />
        </Row>
        <Row style={{ height: 40, marginBottom: 8 }} align="middle">
          <Typography.Text>{I18n.product.images}</Typography.Text>
        </Row>
        <Row style={{ marginBottom: 24 }}>
          {images.map((e, index) => {
            return (
              <input
                id={`image-input${index}`}
                type="file"
                style={{ display: "none" }}
                onChange={(e) => {
                  if (e.target.files !== null) {
                    if (images[index] === undefined) {
                      addImage(e.target.files[0], index);
                    } else {
                      editImage(e.target.files[0], index);
                    }
                  }
                }}
              />
            );
          })}
          {images?.map((image, index) => (
            <Col xs={4} style={{ padding: 2 }}>
              {image === undefined ? (
                <div
                  style={{
                    backgroundColor: "#f9f9f9",
                    width: "100%",
                    minHeight: 150,
                    cursor: areImagesLoading ? "not-allowed" : "pointer",
                    position: "relative",
                  }}
                  onClick={() => {
                    if (!areImagesLoading)
                      document.getElementById(`image-input${index}`)?.click();
                  }}
                >
                  <PlusOutlined
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      WebkitTransform: "translate(-50%, -50%)",
                      transform: "translate(-50%, -50%)",
                    }}
                  />
                </div>
              ) : (
                <Col>
                  <img
                    width="100%"
                    src={
                      isinstanceofProductImage(image)
                        ? (image as ProductImage).url
                        : URL.createObjectURL(image)
                    }
                    alt={index.toString()}
                  />
                  <Row justify="space-around">
                    <Button
                      type="link"
                      disabled={areImagesLoading}
                      onClick={() =>
                        document.getElementById(`image-input${index}`)?.click()
                      }
                    >
                      <EditOutlined />
                    </Button>
                    <Popconfirm
                      title="آیا از حذف این عکس مطمین هستید؟"
                      onConfirm={() => deleteImage(index)}
                      disabled={
                        images.filter((e) => e !== undefined).length === 1 ||
                        areImagesLoading
                      }
                    >
                      <Button
                        type="link"
                        danger
                        disabled={
                          images.filter((e) => e !== undefined).length === 1 ||
                          areImagesLoading
                        }
                      >
                        <DeleteOutlined />
                      </Button>
                    </Popconfirm>
                  </Row>
                </Col>
              )}
            </Col>
          ))}
        </Row>
        <Item
          name="attributes"
          label={
            <Row align="middle">
              {I18n.product.attributes}
              <Button
                style={{ margin: "0 8px" }}
                size="middle"
                type="ghost"
                onClick={() => openSetPriceModal(id as string)}
              >
                تغییر قیمت کلی
              </Button>
              <Button
                size="middle"
                type="ghost"
                onClick={() => openSetOffPriceModal(id as string)}
              >
                تغییر قیمت تخفیف کلی
              </Button>
            </Row>
          }
        >
          <Row align="middle">
            {attributes.map((attribute, index) => (
              <Col key={index} lg={12} md={12}>
                <Card
                  bodyStyle={{ padding: 16 }}
                  style={
                    index % 2 === 0 ? { marginLeft: 12 } : { marginRight: 12 }
                  }
                  actions={[
                    <Button
                      type="link"
                      onClick={() => openEditAttributeModal(attribute)}
                    >
                      <EditOutlined />
                    </Button>,
                    // <Popconfirm
                    //   title="آیا از حذف این ویژگی مطمین هستید؟"
                    //   onConfirm={() => {
                    //     // TODO:
                    //   }}
                    // >
                    //   <Button type="link" danger>
                    //     <DeleteOutlined />
                    //   </Button>
                    // </Popconfirm>,
                  ]}
                >
                  <Row>
                    <div>
                      <Text>{I18n.product.color}</Text>
                      <br />
                      <Text>{I18n.product.size}</Text>
                      <br />
                      <Text>{I18n.product.sizeDescription}</Text>
                      <br />
                      <Text>{I18n.product.count}</Text>
                      <br />
                      <Text>{I18n.product.price}</Text>
                      <br />
                      <Text>{I18n.product.price} تخفیف</Text>
                    </div>
                    <div style={{ paddingRight: 16 }}>
                      <Row align="middle">
                        <div
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: 10,
                            marginLeft: 8,
                            backgroundColor: attribute.color?.color,
                            display: "inline-block",
                          }}
                        />
                        <Text>{attribute.color?.title}</Text>
                      </Row>
                      <Text>{attribute.size}</Text>
                      <br />
                      <Text>{attribute.sizeDescription}</Text>
                      <br />
                      <Text>{attribute.count}</Text>
                      <br />
                      <Text>{attribute.price.toLocaleString()} تومان</Text>
                      <br />
                      <Text>{attribute.offPrice?.toLocaleString()} تومان</Text>
                    </div>
                  </Row>
                </Card>
              </Col>
            ))}
            <Col lg={12} md={12}>
              <Button
                type="link"
                size="middle"
                onClick={openNewAttributeModal}
                icon={<PlusOutlined />}
                block
                style={{ margin: "24px 0" }}
              >
                {I18n.product.addAttribute}
              </Button>
            </Col>
          </Row>
        </Item>
      </Form>

      <NewAttributeModal
        visible={newAttributeModalVisible}
        onSuccess={addAttribute}
        productId={product?.id}
        onClose={closeNewAttributeModal}
      />

      <EditAttributeModal
        visible={editAttributeModal.visible}
        onClose={closeEditAttributeModal}
        attribute={editAttributeModal.attribute}
      />

      <SetPriceModal
        onClose={closeSetPriceModal}
        visible={setPriceModal.visible}
        productId={setPriceModal.productId}
      />

      <SetOffPriceModal
        visible={setOffPriceModal.visible}
        onClose={closeSetOffPriceModal}
        productId={setOffPriceModal.productId}
      />
    </div>
  );
}
